import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Box, SimpleGrid, Text, Flex} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useIntl} from 'react-intl'
import {
    Button,
    Image,
    Link,
    useMultiStyleConfig,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '../../../components/dynamic-image'
import DisplayPrice from '../../../components/display-price'
import {getPriceData} from '../../../utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useTagManager from '../../../hooks/use-tag-manager'
import {getUrl} from '../../../utils/cloudinary-utils'
import {useSiteContext} from '../../../contexts/site'

export const CategoryDetails = ({
    styles,
    colorBarURL,
    products = [],
    category,
    fetchMore,
    hasMore
}) => {
    const intl = useIntl()
    const infiniteScrollStyles = useStyleConfig('InfiniteScrollStyles')
    const productListStyles = useStyleConfig('ProductListStyles')
    const visibleProducts = styles.filter((style) =>
        products.find((product) => style.SKUforStylePrice === product.id)
    )

    return (
        <Flex
            className="cb-plp-container"
            direction="column"
            minHeight={{base: '600px', lg: '800px'}}
        >
            <SimpleGrid columns={{base: 2, md: 4}} spacing={2}>
                {visibleProducts.map((style, idx) => (
                    <HoverImageCard
                        key={idx}
                        id={idx}
                        style={style}
                        colorBarURL={colorBarURL}
                        products={products}
                        category={category}
                    />
                ))}
            </SimpleGrid>

            {hasMore && (
                <Box data-testid="sf-cb-infinite-scroll" {...infiniteScrollStyles.wrapper}>
                    {hasMore && (
                        <Flex {...productListStyles.footer}>
                            <Button
                                aria-label={intl.formatMessage({
                                    id: 'plp_view_more',
                                    defaultMessage: 'View More'
                                })}
                                onClick={fetchMore}
                                {...infiniteScrollStyles.button}
                            >
                                {intl.formatMessage({
                                    id: 'plp_view_more',
                                    defaultMessage: 'View More'
                                })}
                            </Button>
                        </Flex>
                    )}
                </Box>
            )}
        </Flex>
    )
}

const HoverImageCard = ({idx, style, colorBarURL, products, category}) => {
    const [imageIndex, setImageIndex] = useState(0)
    const {currency: activeCurrency} = useCurrency()
    const tagManager = useTagManager()
    const {isYR} = useSiteContext()
    const productTileStyles = useMultiStyleConfig('ProductTileStyles')
    const pageCategory = 'colorbar'

    const handleMouseEnter = () => {
        if (style.sneakPeek?.length) {
            setImageIndex((prev) => (prev + 1) % style.sneakPeek.length)
        }
    }

    const handleIconClick = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const productData = products?.find((product) => product.id === style.SKUforStylePrice)
    const currentImage = style.sneakPeek?.[imageIndex]?.imageUrl || style.img
    const url = `${colorBarURL}?product=${
        style.productId
    }&customer=1613&cbCategory=${encodeURIComponent(category)}`
    const withDpr = (url) => url.replace('/upload/', '/upload/dpr_1.2/')
    const imageUrlWithDpr = withDpr(currentImage)
    const imageProps = {
        background: 'gray.100',
        widths: ['225w', '325w', '384w', '576w', '650w', '768w', '960w'],
        imgSizes: '(min-width: 80em) 25vw, (min-width: 62em) 25vw, (min-width: 48em) 25vw, 50vw',
        imageProps: {
            alt: style.name,
            loading: 'lazy',
            height: 'auto',
            width: '100%',
            mixBlendMode: 'multiply',
            aspectRatio: {
                base: '200/200',
                md: '1/1'
            }
        }
    }

    const priceData = getPriceData(productData)

    // We'll just display one price on the CB PLP.
    if (priceData) {
        priceData.isOnSale = false
    }
    if (priceData?.pricePerUnit) {
        priceData.currentPrice = priceData.pricePerUnit // If price in the sales price book is configured, please prefer that one.
    }
    const breadcrumb = `${pageCategory} / ${category}`

    return (
        <Link
            href={url?.split('.com')?.[1]}
            _hover={{textDecoration: 'none'}}
            overflow="hidden"
            display="block"
            onClick={() => {
                tagManager.productClick(
                    'colorbar',
                    style,
                    null,
                    idx + 1,
                    breadcrumb,
                    category,
                    pageCategory
                )
            }}
        >
            <Box {...productTileStyles.imageWrapper} className="image-wrapper">
                <Box
                    position="absolute"
                    top="10px"
                    right="10px"
                    bg="white"
                    borderRadius="full"
                    cursor="pointer"
                    onClick={handleIconClick}
                    zIndex={1}
                >
                    <Text
                        color="black.100"
                        padding="6px 20px"
                        display="flex"
                        alignItems="center"
                        fontSize="12px"
                    >
                        Customize
                        <Image
                            ml="2"
                            src={getUrl('UIImages/images/cb-wheel.png')}
                            alt="Customize Color Bar"
                            width="16px"
                            height="16px"
                            borderRadius="50%"
                            borderWidth="1px"
                        />
                    </Text>
                </Box>

                <DynamicImage
                    data-testid="cb-child-product-image"
                    display="block"
                    isolation="isolate"
                    src={imageUrlWithDpr}
                    onMouseEnter={handleMouseEnter}
                    {...imageProps}
                />

                {!isYR && productData?.c_isYellowRoseProduct && (
                    <Image
                        className="yellow-rose-brand-icon"
                        {...productTileStyles.yrBrandIcon}
                        bottom="10px"
                        top="unset"
                        alt="Yellow Rose"
                        src={getUrl('UIImages/images/yr-brand-icon.svg')}
                    />
                )}
            </Box>

            <Box p={2}>
                <Text
                    fontFamily="cormorant"
                    fontWeight="400"
                    letterSpacing="1px"
                    fontSize="18px"
                    color="black.100"
                    mb={1}
                >
                    {style.name}
                </Text>
                <Box
                    color="black.100"
                    fontWeight="300"
                    display="flex"
                    fontSize="14px"
                    sx={{'.current-price': {fontSize: '14px;'}}}
                >
                    <Text mr={1}>
                        <FormattedMessage id="starting.at" defaultMessage="starting at" />
                    </Text>

                    <DisplayPrice
                        priceData={priceData}
                        currency={productData?.c_selectedCurrency || activeCurrency}
                        symbol={productData?.c_selectedCurrencySymbol}
                    />
                </Box>
            </Box>
        </Link>
    )
}

CategoryDetails.propTypes = {
    styles: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            productId: PropTypes.string,
            recipe: PropTypes.string,
            img: PropTypes.string,
            sneakPeek: PropTypes.arrayOf(
                PropTypes.shape({
                    variantName: PropTypes.string,
                    imageUrl: PropTypes.string
                })
            ),
            description: PropTypes.string,
            SKUforStylePrice: PropTypes.string,
            skus: PropTypes.arrayOf(PropTypes.string)
        })
    ),
    colorBarURL: PropTypes.string,
    products: PropTypes.array,
    category: PropTypes.string,
    fetchMore: PropTypes.func,
    isFetchingMore: PropTypes.bool,
    hasMore: PropTypes.bool
}

HoverImageCard.propTypes = {
    idx: PropTypes.number,
    style: PropTypes.shape({
        name: PropTypes.string,
        productId: PropTypes.string,
        recipe: PropTypes.string,
        img: PropTypes.string,
        sneakPeek: PropTypes.arrayOf(
            PropTypes.shape({
                variantName: PropTypes.string,
                imageUrl: PropTypes.string
            })
        ),
        description: PropTypes.string,
        SKUforStylePrice: PropTypes.string,
        skus: PropTypes.arrayOf(PropTypes.string)
    }),
    colorBarURL: PropTypes.string,
    products: PropTypes.array,
    key: PropTypes.string,
    category: PropTypes.string
}
